import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Megaphone, Plus, RefreshCw, CheckCircle, XCircle, Clock, Radio } from 'lucide-react';
import toast from 'react-hot-toast';
import { airportAnnouncementsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import PageHeader from '../../components/UI/PageHeader';
import EmptyState from '../../components/UI/EmptyState';
import StatusBadge from '../../components/UI/StatusBadge';
import Button from '../../components/UI/Button';

// Same tones as OperatorApprovalsPage's "My Submissions" tab (same underlying
// AirportAnnouncement.status values, shown on a different page) — 'broadcasting'
// used to be purple here vs. a solid blue badge there; both now match the
// "happening right now" convention used everywhere else (light tint + pulsing dot).
const STATUS_META = {
  pending:      { label: 'Pending',      tone: 'yellow' },
  generating:   { label: 'Generating',   tone: 'info' },
  ready:        { label: 'Ready',        tone: 'success' },
  broadcasting: { label: 'Broadcasting', tone: 'info', pulse: true },
  played:       { label: 'Played',       tone: 'neutral' },
  rejected:     { label: 'Rejected',     tone: 'danger' },
  cancelled:    { label: 'Cancelled',    tone: 'neutral' },
};

const PRIORITY_DOT = {
  normal:    'bg-gray-400',
  urgent:    'bg-orange-400',
  emergency: 'bg-red-500',
};

export default function OperatorAnnouncementsPage() {
  const navigate = useNavigate();
  const { isOperator } = useAuth();
  const newAnnouncePath = isOperator() ? '/operator/announce' : '/announce';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await airportAnnouncementsAPI.getAll({ limit: 50 });
      setItems(res.data.data || res.data || []);
    } catch {
      toast.error('Failed to load announcements');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleCancel(id) {
    if (!window.confirm('Cancel this announcement?')) return;
    try {
      await airportAnnouncementsAPI.cancel(id);
      toast.success('Cancelled');
      load();
    } catch (err) {
      if (!err._toasted) toast.error('Failed to cancel');
    }
  }

  return (
    <div className="max-w-xl mx-auto px-4 py-6">
      <PageHeader
        title="My Announcements"
        subtitle="Submissions you have made"
        actions={
          <>
            <button onClick={load} className="p-2 rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50">
              <RefreshCw size={16} />
            </button>
            <Button variant="primary" icon={Plus} onClick={() => navigate(newAnnouncePath)}>
              New
            </Button>
          </>
        }
      />

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={Megaphone}
          title="No announcements yet"
          actionLabel="Submit your first announcement"
          onAction={() => navigate(newAnnouncePath)}
        />
      ) : (
        <div className="space-y-2">
          {items.map(item => {
            const meta = STATUS_META[item.status] || STATUS_META.pending;
            const dot  = PRIORITY_DOT[item.priority] || 'bg-gray-400';
            const isPending = item.status === 'pending';
            return (
              <div key={item.id} className="bg-white rounded-2xl border border-gray-200 px-4 py-3.5">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${dot}`} />
                      <span className="font-semibold text-sm text-gray-900 truncate">{item.title}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <StatusBadge label={meta.label} tone={meta.tone} dot={meta.pulse} pulse={meta.pulse} />
                      <span className="text-xs text-gray-400 capitalize">{item.announcement_type?.replace('_', ' ')}</span>
                      <span className="text-xs text-gray-400">
                        {new Date(item.created_at || item.createdAt).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    {item.admin_notes && (
                      <p className="text-xs text-red-600 mt-1.5 italic">Note: {item.admin_notes}</p>
                    )}
                  </div>
                  {isPending && (
                    <button
                      onClick={() => handleCancel(item.id)}
                      className="flex-shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                      title="Cancel"
                    >
                      <XCircle size={16} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
